import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  itemPickerApi, warehousesApi, transferRequisitionsApi,
  type ItemPickerRow,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface DraftLine { key: string; item: ItemPickerRow | null; qty: number; reason: string }
const blank = (): DraftLine => ({ key: crypto.randomUUID(), item: null, qty: 0, reason: "" });

/**
 * UC-M2-TR-03 — Branch requests stock from another warehouse. The Suggested source
 * warehouse helps the central store route the request; final source is chosen at
 * Convert-time when the requisition is turned into a TransferOrder.
 */
export function CreateTransferRequisitionPage() {
  const navigate = useNavigate();
  const [requestingWarehouseId, setRequestingWarehouseId] = useState("");
  const [suggestedSourceWarehouseId, setSuggestedSourceWarehouseId] = useState("");
  const [neededByDate, setNeededByDate] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });
  // Item picker scoped to the suggested source warehouse so users see realistic candidates.
  const picker = useQuery({
    queryKey: ["item-picker", suggestedSourceWarehouseId || "any"],
    queryFn: () => itemPickerApi.picker({
      stockPolicy: suggestedSourceWarehouseId ? "Source" : "Any",
      warehouseId: suggestedSourceWarehouseId || requestingWarehouseId,
      pageSize: 50,
    }),
    enabled: !!(requestingWarehouseId || suggestedSourceWarehouseId),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const validationError =
    !requestingWarehouseId ? "Pick a requesting warehouse." :
    !lines.every(l => l.item && l.qty > 0) ? "Every line needs an item and a quantity greater than 0." :
    null;

  const mutation = useMutation({
    meta: { successMessage: "Transfer requisition created" },
    mutationFn: () => transferRequisitionsApi.create({
      requestingWarehouseId,
      suggestedSourceWarehouseId: suggestedSourceWarehouseId || undefined,
      neededByDate: neededByDate || undefined,
      reason: reason || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, qty: l.qty, reason: l.reason || undefined,
      })),
    }),
    onSuccess: (r) => navigate(`/warehouse/transfer-requisitions/${r.id}`),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New transfer requisition</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Requesting warehouse *" size="small" sx={{ minWidth: 240 }}
            value={requestingWarehouseId} onChange={(e) => setRequestingWarehouseId(e.target.value)}>
            <MenuItem value="">— pick warehouse —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Suggested source" size="small" sx={{ minWidth: 240 }}
            value={suggestedSourceWarehouseId}
            onChange={(e) => setSuggestedSourceWarehouseId(e.target.value)}>
            <MenuItem value="">— none —</MenuItem>
            {warehouses.data?.items.filter(w => w.id !== requestingWarehouseId).map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Needed by" size="small" InputLabelProps={{ shrink: true }}
            value={neededByDate} onChange={(e) => setNeededByDate(e.target.value)} />
        </Stack>
        <TextField label="Reason" fullWidth multiline minRows={2}
          value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])}>Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 320 }}>Item *</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>Qty *</TableCell>
            <TableCell>Line reason</TableCell>
            <TableCell sx={{ width: 60 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={picker.data ?? []}
                    getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                    value={l.item}
                    onChange={(_, v) => update(l.key, { item: v })}
                    renderInput={(p) => <TextField {...p} placeholder={`Line ${idx + 1}`} />}
                    loading={picker.isLoading}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 100 }}
                    value={l.qty} onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.reason}
                    onChange={(e) => update(l.key, { reason: e.target.value })} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/warehouse/transfer-requisitions")}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>Save draft requisition</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
